import { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Clock, Mail, Phone, Building, BookOpen, Trash2 } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PartnerProposalsPage({ admin, onLogout }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await axios.get(`${API}/partner/proposals`);
      setProposals(response.data);
    } catch (error) {
      console.error('Error fetching proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (proposalId, newStatus) => {
    try {
      await axios.put(`${API}/partner/proposals/${proposalId}`, { status: newStatus });
      fetchProposals();
      if (newStatus === 'approved') {
        alert('✅ Proposition approuvée ! Une formation a été créée automatiquement en mode "Brouillon" dans les Formations.');
      }
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const deleteProposal = async (proposalId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette proposition ?')) return;
    try {
      await axios.delete(`${API}/partner/proposals/${proposalId}`);
      fetchProposals();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'En attente' },
      approved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Approuvée' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Refusée' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text} flex items-center gap-1 w-fit`}>
        <Icon size={14} />
        {badge.label}
      </span>
    );
  };

  const filtered = proposals.filter(p => filterStatus === 'all' || p.status === filterStatus);

  return (
    <AdminLayout admin={admin} onLogout={onLogout} title="Propositions Partenaires">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-md border border-yellow-100">
            <p className="text-sm text-gray-600">En attente</p>
            <p className="text-2xl font-bold text-yellow-600" data-testid="stat-pending-proposals">
              {proposals.filter(p => p.status === 'pending').length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md border border-green-100">
            <p className="text-sm text-gray-600">Approuvées</p>
            <p className="text-2xl font-bold text-green-600" data-testid="stat-approved-proposals">
              {proposals.filter(p => p.status === 'approved').length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md border border-red-100">
            <p className="text-sm text-gray-600">Refusées</p>
            <p className="text-2xl font-bold text-red-600" data-testid="stat-rejected-proposals">
              {proposals.filter(p => p.status === 'rejected').length}
            </p>
          </div>
        </div>

        {/* Filter */}
        <div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500"
            data-testid="filter-proposal-status"
          >
            <option value="all">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="approved">Approuvées</option>
            <option value="rejected">Refusées</option>
          </select>
        </div>

        {/* Proposals List */}
        {loading ? (
          <div className="text-center py-12"><p className="text-gray-500">Chargement...</p></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-violet-100">
            <BookOpen className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">Aucune proposition</p>
            <p className="text-sm text-gray-400 mt-2">
              Partagez ce lien avec vos partenaires : <span className="text-violet-600">/partenaire</span>
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(proposal => (
              <div key={proposal.id} className="bg-white rounded-xl p-6 shadow-md border border-violet-100" data-testid={`proposal-${proposal.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{proposal.training_title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{proposal.training_category}</p>
                  </div>
                  {getStatusBadge(proposal.status)}
                </div>

                <p className="text-gray-600 text-sm mb-4">{proposal.training_description}</p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Durée</p>
                    <p className="font-semibold">{proposal.duration_hours}h</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Prix</p>
                    <p className="font-semibold text-violet-600">{proposal.proposed_price}€</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Max participants</p>
                    <p className="font-semibold">{proposal.max_participants}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Lieu</p>
                    <p className="font-semibold truncate">{proposal.location}</p>
                  </div>
                </div>

                <div className="bg-violet-50 rounded-lg p-4 mb-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Contact partenaire</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Building size={14} />
                      <span>{proposal.partner_name}{proposal.organization ? ` (${proposal.organization})` : ''}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail size={14} />
                      <span className="truncate">{proposal.partner_email}</span>
                    </div>
                    {proposal.partner_phone && (
                      <div className="flex items-center gap-2">
                        <Phone size={14} />
                        <span>{proposal.partner_phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                {proposal.additional_notes && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-1">Notes</p>
                    <p className="text-sm text-gray-600">{proposal.additional_notes}</p>
                  </div>
                )}

                {proposal.status === 'pending' && (
                  <div className="flex gap-2 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => updateStatus(proposal.id, 'approved')}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                      data-testid={`approve-${proposal.id}`}
                    >
                      <CheckCircle size={16} />
                      Approuver
                    </button>
                    <button
                      onClick={() => updateStatus(proposal.id, 'rejected')}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      data-testid={`reject-${proposal.id}`}
                    >
                      <XCircle size={16} />
                      Refuser
                    </button>
                    <button
                      onClick={() => deleteProposal(proposal.id)}
                      className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}

                {proposal.status !== 'pending' && (
                  <div className="flex justify-end pt-4 border-t border-gray-100">
                    <button
                      onClick={() => deleteProposal(proposal.id)}
                      className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2 text-sm"
                    >
                      <Trash2 size={14} />
                      Supprimer
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
